import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEnv } from '@/src/application/hooks/util/useEnv';
import configData from '@/src/config.json';
import { APIService } from '@/src/infrastructure/Protocol/APIService';

import { INotificationsService } from './INotificationsService';
import { NotificationsService } from './NotificationsService';
import { StubNotificationsService } from './StubNotificationsService';

export const useNotificationsService = () => {
  const { env } = useEnv();

  const service = useMemo(() => {
    if (env !== 'storybook') {
      const { URL_EMA_APP_V2, JWT_DEV_TOKEN } = configData.endpoints[env];
      const jwt =
        JWT_DEV_TOKEN !== ''
          ? JWT_DEV_TOKEN
          : (window as Window & typeof globalThis & { __JWTToken: string }).__JWTToken;

      return NotificationsService.create(new APIService(URL_EMA_APP_V2, jwt));
    } else {
      return StubNotificationsService.create();
    }
  }, [env]);

  return service;
};

export const useNotificationsGetterService = <T extends keyof INotificationsService>(
  method: T,
  ...p: Parameters<INotificationsService[T]>
): {
  data: Awaited<ReturnType<INotificationsService[T]>> | undefined;
  error: boolean;
  loading: boolean;
  reFetchData: () => void;
} => {
  const service = useNotificationsService();
  const [data, setData] = useState<Awaited<ReturnType<INotificationsService[T]>>>();
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const params = JSON.stringify(p);

  const fetchData = useCallback(async () => {
    if (!service) throw new Error(`EM_Error The ${typeof service} does not exist`);

    const fn: unknown = service[method];

    if (typeof fn !== 'function')
      throw new Error(`Method '${method}' does not exist on service '${typeof service}'`);

    try {
      setLoading(true);
      const result = (await fn.call(
        service,
        ...(JSON.parse(params) as Parameters<INotificationsService[T]>)
      )) as Awaited<ReturnType<INotificationsService[T]>>;

      if (!result) throw new Error('Error Notifications Service');

      setData(result);
      setLoading(false);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [service, method, params]);

  const reFetchData = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
  }, [method, service, params, fetchData]);

  return { data, error, loading, reFetchData };
};
